<script setup>
import {
  Breadcrumb,
  PageHeading,
  Toggle,
  Button,
  Label,
  Select,
  Spin,
} from "@components";
import { onMounted, ref, watch, reactive, computed } from "vue";
import Prompt from "@utils/Prompt";
import AddonItem from "./AddonItem.vue";

import { useSettings } from "@store/settings";
const $settings = useSettings();

import { useStaff } from "@store/staff";
const $staff = useStaff();

import { useAppointment } from "@store/appointment";
const $appointment = useAppointment();

onMounted(async () => {
  await $settings.fetchSettings();
  await $staff.fetchData();
  await $appointment.fetchData();
});

const addons = ref([
  {
    id: "staffs",
    svg: "addonStaff",
    name: `${$settings.labels.staff?.plural}`,
    details: `Add and manage multiple ${String(
      $settings.labels.staff?.plural
    ).toLowerCase()} for your business. Disable if you are an individual ${String(
      $settings.labels.staff?.singular
    ).toLowerCase()}`,
    status: $settings.settings.addons.staffs || false,
  },
  {
    id: "holidays_days_off",
    svg: "addonHoliday",
    name: "Holidays & Days Off",
    details: `Manage your business's non-working days. Set holidays or days off to prevent bookings during these periods`,
    status: $settings.settings.addons.holidays_days_off || false,
  },
  {
    id: "calendar",
    svg: "addonCalendar",
    name: "Calendar",
    details: `Access a dedicated PreBook calendar to view and manage ${String(
      $settings.labels.appointment?.plural
    ).toLowerCase()}. Disable if you prefer external calendar solutions`,
    status: $settings.settings.addons.calendar || false,
  },
  {
    id: "payment",
    svg: "addonPayment",
    name: `${$settings.labels.payment?.singular}`,
    details: `Accept online or offline ${String(
      $settings.labels.payment?.plural
    ).toLowerCase()} for ${String(
      $settings.labels.service?.plural
    ).toLowerCase()}. Disable if you don't require payment processing for your bookings`,
    status: $settings.settings.addons.payment || false,
  },
  {
    id: "chat_widget",
    svg: "addonChatWidget",
    name: `Chat Widget`,
    details: `Disable if you don't require chat widget for your bookings`,
    status: $settings.settings.addons.chat_widget || false,
  },
]);

const showAlert = (addon) => {
  return new Promise((resolve) => {
    const resolveMessage = (message) => resolve({ success: true, message });

    const { id, status } = addon;

    switch (id) {
      case "staffs": {
        // bail if service provider info is not filled up
        let hasServiceProvider =
          $settings.business.provider_first_name &&
          $settings.business.provider_last_name &&
          $settings.business.provider_display_name;
        if (status === true && !hasServiceProvider) {
          revertAddonStatus(addon);
          return Toast.warning(
            `Please fill up your ${String(
              $settings.labels.staff?.singular
            ).toLowerCase()} info first!`
          );
        }

        const hasStaff = $staff.getStaffs.length > 0;

        const message = status
          ? hasServiceProvider
            ? `Enabling ${String(
                $settings.labels.staff?.plural
              ).toLowerCase()} will refresh the page and switch to a business setup,<br> allowing you to add and manage multiple ${String(
                $settings.labels.staff?.plural
              ).toLowerCase()}.<br> Your current ${String(
                $settings.labels.service?.singular
              ).toLowerCase()} provider will become a ${
                $settings.labels.staff?.singular
              }.`
            : !hasStaff
            ? `Enabling ${String(
                $settings.labels.staff?.plural
              ).toLowerCase()} will refresh the page and switch to a business setup,<br> allowing you to add and manage multiple ${String(
                $settings.labels.staff?.plural
              ).toLowerCase()}.`
            : "Unknown Error"
          : hasStaff
          ? `Disabling ${String(
              $settings.labels.staff?.plural
            ).toLowerCase()} will refresh the page and switch to an individual setup.<br> The ${String(
              $settings.labels.staff?.plural
            ).toLowerCase()} menu will be hidden, but existing ${String(
              $settings.labels.staff?.plural
            ).toLowerCase()} data<br> will remain. Continue to proceed.`
          : `Disabling ${String(
              $settings.labels.staff?.plural
            ).toLowerCase()} will refresh the page and switch to an individual setup.<br> The ${String(
              $settings.labels.staff?.plural
            ).toLowerCase()} menu will be hidden. Proceed if you're a solo<br> ${String(
              $settings.labels.staff?.singular
            ).toLowerCase()}. You can re-enable it anytime.`;
        return resolveMessage(message);
      }

      case "holidays_days_off": {
        const hasHolidays = $settings.settings.days_off?.holidays?.length > 0;
        let message = status
          ? hasHolidays
            ? `Enabling Holidays & Days Off will reactivate all previously added holidays<br>
               and days off. You won't receive bookings on these days.`
            : ""
          : hasHolidays
          ? `Disabling Holidays & Days Off will make existing holidays and days off inactive.<br>
             You'll receive bookings on these days. Existing data will be<br>
             preserved if you re-enable it later.`
          : `Disabling Holidays & Days Off will allow bookings on all days, including holidays<br>
             and days off. You can re-enable this feature later if needed.`;
        return resolveMessage(message);
      }

      case "calendar": {
        let message = status
          ? ""
          : `Disabling Calendar will hide the calendar menu and prevent you from viewing or<br>
           managing ${String(
             $settings.labels.appointment?.plural
           ).toLowerCase()} on the PreBook calendar. You can<br>
           re-enable this feature later if needed.`;
        return resolveMessage(message);
      }

      case "payment": {
        let message = status
          ? ""
          : `Disabling the ${String(
              $settings.labels.payment?.singular
            ).toLowerCase()} feature will hide the ${String(
              $settings.labels.payment?.singular
            ).toLowerCase()} options and prevent you<br>
           from collecting or entering any ${String(
             $settings.labels.payment?.plural
           ).toLowerCase()} details for bookings. If you don’t need<br>
           to collect ${String(
             $settings.labels.payment?.plural
           ).toLowerCase()} from ${String(
              $settings.labels.customer?.plural
            ).toLowerCase()} or enter ${String(
              $settings.labels.payment?.plural
            ).toLowerCase()} information, you can disable this feature.<br>
           ${String(
             $settings.labels.service?.singular
           )} pricing remains visible. ${String(
              $settings.labels.payment?.plural
            ).toLowerCase()} data is preserved. Re-enable anytime if needed.`;
        return resolveMessage(message);
      }

      case "chat_widget": {
        let message = status
          ? ""
          : "Disable if you don't require chat widget for your bookings";
        return resolveMessage(message);
      }

      default:
        resolve({ success: false });
    }
  });
};

const toggleAddon = async (addon) => {
  console.log("Toggled", addon);

  $settings.settings.addons[addon.id] = addon.status;

  try {
    const response = await showAlert(addon);
    console.log(
      "response",
      response,
      response.success && response.message,
      response.success && !response.message
    );

    if (response.success) {
      const choice = response.message
        ? await Prompt.fire({
            title: "Are you sure?",
            message: response.message,
            ok: "Yes, proceed",
            cancel: "No, cancel",
          })
        : { ok: true };

      if (choice.ok) {
        await saveAndReload(addon);
      } else {
        revertAddonStatus(addon);
      }
    } else {
      console.error(
        "prebook",
        "something went wrong on saving the addon settings"
      );
    }
  } catch (error) {
    console.error("toggleAddon error:", error);
  }
};

const createServiceProviderStaffAndAssign = () => {
  return new Promise(async (resolve) => {
    let response = await $staff.updateStaff({
      display_name: $settings.business.provider_display_name,
      meta: {
        color: "#875BF7",
        first_name: $settings.business.provider_first_name,
        last_name: $settings.business.provider_last_name,
        title: "",
        highlights: "",
        email: $settings.business.email,
        phone: $settings.business.phone,
        bio: $settings.business.provider_bio,
        status: "publish",
      },
    });

    // response.data.staff_id

    let unAssignedAppointments = $appointment.getAppointments.filter(
      (ap) => ap.staff_id == "0"
    );

    console.log("unAssignedAppointments", unAssignedAppointments);
    await unAssignedAppointments.forEach(async (ap) => {
      // await $appointment.model().create({
      //     ...ap,
      //     staff_id: response.data.staff_id
      // })

      await $appointment.model().update(
        {
          ...ap,
          staff_id: response.data.staff_id,
        },
        ap.appointment_id
      );
    });

    resolve({ success: true });
  });
};

const saveAndReload = async (addon) => {
  $settings.addons[addon.id] = addon.status;
  await $settings.saveSettings("addons", $settings.addons);

  if (addon.id == "staffs" && addon.status == true) {
    await createServiceProviderStaffAndAssign().then(() => {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    });
    return;
  }

  setTimeout(() => {
    window.location.reload();
  }, 1000);
};

const revertAddonStatus = (addon) => {
  const targetAddon = addons.value.find((a) => a.id === addon.id);
  if (targetAddon) {
    targetAddon.status = !addon.status;
  }
  $settings.settings.addons[addon.id] = addon.status;
};
</script>

<template>
  <div class="md:max-w-[992px] m-auto mt-[-22px]">
    <!-- breadcrumb -->
    <Breadcrumb
      :items="[
        { icon: true, name: 'settings01', route: { name: 'settings' } },
        { name: 'Addons', route: { name: 'settings.addons' } },
      ]"
    />

    <!-- page title -->
    <PageHeading title="Addons" :divider="false" class="pb-2" />
    <!-- {{ addons }} -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-5">
      <AddonItem
        v-for="addon in addons"
        :key="addon"
        :addon="addon"
        @change="toggleAddon(addon)"
      />
    </div>
  </div>
</template>

<style></style>
